import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface EmptyStateProps extends Omit<ComponentProps<'div'>, 'title'> {
  icon?: ReactNode;
  /** Ilustración propia (ver `illustrations.tsx`); reemplaza al dibujo genérico con el ícono. */
  illustration?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

/**
 * Ilustración del estado vacío: dos láminas apiladas (el "documento" que todavía no existe) y una
 * pista de circuito con nodos, el mismo trazo del isotipo de la marca, que termina en el ícono.
 * Todo en tokens (`currentColor` + opacidades) para que funcione igual en claro y oscuro.
 */
function EmptyArt({ icon }: { icon: ReactNode }) {
  return (
    <div aria-hidden className="relative h-24 w-36 shrink-0 text-highlight">
      <svg viewBox="0 0 144 96" fill="none" className="absolute inset-0 size-full">
        <rect
          x="30"
          y="14"
          width="84"
          height="62"
          rx="10"
          className="fill-card stroke-border"
          transform="rotate(-6 72 45)"
        />
        <rect x="30" y="16" width="84" height="62" rx="10" className="fill-card stroke-border" />
        <path
          d="M44 32h34M44 42h22"
          className="stroke-muted-foreground/25"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path
          d="M6 70h14l8-8M138 22h-12l-8 8M118 70h20"
          stroke="currentColor"
          strokeOpacity="0.45"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="6" cy="70" r="2.5" fill="currentColor" fillOpacity="0.6" />
        <circle cx="138" cy="22" r="2.5" fill="currentColor" fillOpacity="0.6" />
        <circle cx="138" cy="70" r="2.5" fill="currentColor" fillOpacity="0.6" />
      </svg>
      <span className="absolute right-3 bottom-1 flex size-11 items-center justify-center rounded-full bg-card text-highlight shadow-raised ring-1 ring-highlight/20 [&_svg]:size-5">
        {icon}
      </span>
    </div>
  );
}

export function EmptyState({
  icon,
  illustration,
  title,
  description,
  action,
  className,
  children,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-4 rounded-xl border border-dashed bg-card/60 px-6 py-8 text-center sm:py-10',
        className,
      )}
      {...props}
    >
      {illustration ?? (icon ? <EmptyArt icon={icon} /> : null)}
      <div className="flex max-w-prose flex-col items-center gap-1.5">
        <h3 className="text-base font-semibold tracking-tight">{title}</h3>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {children}
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}
